<template>
   <v-layout align-start>
      <v-flex xs12>
         <v-snackbar
            v-model="snackbar"
            :color="snackColor"
            :right="true"
            :bottom="true"
            :timeout="4000"
         >
            {{ snackText }}
            <template #action="{ attrs }">
               <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
                  Cerrar
               </v-btn>
            </template>
         </v-snackbar>

         <v-card elevation="0" style="position: relative">

            <!-- Loading overlay -->
            <v-overlay :value="loading" absolute color="white" opacity="0.85" z-index="4">
               <div class="d-flex flex-column align-center">
                  <v-progress-circular
                     indeterminate
                     color="primary"
                     size="64"
                     width="5"
                  ></v-progress-circular>
                  <span class="mt-4 primary--text font-weight-medium" style="font-size: 15px">
                     Cargando talonarios...
                  </span>
                  <span class="mt-1 grey--text caption">{{ selectCiclo }}</span>
               </div>
            </v-overlay>

            <v-toolbar color="primary" dark>
               <v-toolbar-title style="font-weight: 500">
                  Talonarios Colecturía Digital
               </v-toolbar-title>
               <v-divider class="mx-3" inset vertical></v-divider>

               <!-- Selector de ciclo -->
               <div class="mt-6" style="width: 170px">
                  <v-autocomplete
                     v-model="selectCiclo"
                     :items="cmbCiclo"
                     color="white"
                     item-text="nombre"
                     item-value="nombre"
                     label="Ciclo"
                     no-data-text="Sin resultados"
                     placeholder="Seleccionar..."
                  ></v-autocomplete>
               </div>

               <!-- Selector de carrera -->
               <div class="mt-6 ml-3" style="width: 260px">
                  <v-autocomplete
                     v-model="selectCarrera"
                     :items="cmbCarrera"
                     color="white"
                     item-text="nombre"
                     item-value="codigo"
                     label="Carrera"
                     no-data-text="Sin resultados"
                     placeholder="Todas las carreras"
                     clearable
                     @change="listarContratos"
                  ></v-autocomplete>
               </div>

               <v-spacer></v-spacer>

               <!-- Búsqueda -->
               <v-text-field
                  v-model="search"
                  class="mt-3"
                  color="white"
                  append-icon="fa-search"
                  label="Buscar alumno o carnet"
                  single-line
                  clearable
               ></v-text-field>
            </v-toolbar>

            <!-- Resumen rápido -->
            <v-row class="ma-0 px-4 py-2" style="background: #f5f5f5">
               <v-col cols="auto" class="py-2">
                  <span class="caption grey--text">Talonarios:</span>
                  <span class="ml-1 font-weight-bold">{{ contratos.length }}</span>
               </v-col>
               <v-divider vertical class="my-1"></v-divider>
               <v-col cols="auto" class="py-2">
                  <span class="caption grey--text">Activos:</span>
                  <span class="ml-1 font-weight-bold primary--text">{{ totalActivos }}</span>
               </v-col>
               <v-divider vertical class="my-1"></v-divider>
               <v-col cols="auto" class="py-2">
                  <span class="caption grey--text">Anulados:</span>
                  <span class="ml-1 font-weight-bold red--text darken-1">{{ totalAnulados }}</span>
               </v-col>
               <v-divider vertical class="my-1"></v-divider>
               <v-col cols="auto" class="py-2">
                  <span class="caption grey--text">Proceso de graduación:</span>
                  <span class="ml-1 font-weight-bold">{{ totalGraduacion }}</span>
               </v-col>
            </v-row>

            <v-divider></v-divider>

            <!-- Data table -->
            <v-data-table
               :headers="headers"
               :items="contratos"
               :search="search"
               no-data-text="No hay talonarios para el ciclo seleccionado"
               no-results-text="Búsqueda sin resultados"
               :footer-props="{
                  itemsPerPageOptions: [15, 25, 50, 100],
                  itemsPerPageText: 'Filas por página:'
               }"
               :items-per-page="15"
               class="elevation-1"
            >
               <template #item.estado="{ item }">
                  <v-chip
                     :color="item.estado === 'A' ? 'primary' : 'red darken-1'"
                     dark
                     x-small
                     label
                  >
                     {{ item.estado === 'A' ? 'Activo' : item.estado }}
                  </v-chip>
               </template>

               <template #item.procesoGraduacion="{ item }">
                  <span v-if="item.procesoGraduacion" class="primary--text font-weight-medium">Sí</span>
                  <span v-else class="grey--text">—</span>
               </template>

               <template #item.opciones="{ item }">
                  <!-- Colecturía Externa (CodigoUnidadEducativa = 001) -->
                  <v-tooltip v-if="item.codigoUnidadEducativa === '001'" bottom color="orange darken-3">
                     <template #activator="{ on }">
                        <v-btn
                           text
                           small
                           fab
                           color="orange darken-3"
                           v-on="on"
                           @click="irColecturiaExterna(item)"
                        >
                           <v-icon small>fas fa-external-link-alt</v-icon>
                        </v-btn>
                     </template>
                     <span>Colecturía Externa</span>
                  </v-tooltip>

                  <!-- Colecturía Digital (resto de carreras) -->
                  <v-tooltip v-else bottom color="primary">
                     <template #activator="{ on }">
                        <v-btn
                           text
                           small
                           fab
                           color="primary"
                           :loading="loadingAccess[item.cliente] === 'colecturia'"
                           v-on="on"
                           @click="irColecturiaDigital(item)"
                        >
                           <v-icon small>fa-store</v-icon>
                        </v-btn>
                     </template>
                     <span>Colecturía Digital</span>
                  </v-tooltip>

                  <v-tooltip v-if="item.codigoUnidadEducativa !== '001'" bottom color="primary">
                     <template #activator="{ on }">
                        <v-btn
                           text
                           small
                           fab
                           color="amber darken-3"
                           :loading="loadingAccess[item.cliente] === 'reporte'"
                           v-on="on"
                           @click="irReporteTalonario(item)"
                        >
                           <v-icon small>fa-file-alt</v-icon>
                        </v-btn>
                     </template>
                     <span>Reporte de talonario</span>
                  </v-tooltip>
               </template>

            </v-data-table>
         </v-card>
      </v-flex>
   </v-layout>
</template>

<script>
import axios from 'axios'
import { authComputed } from '../helpers'
import formatters from '../mixins/formattersLib'

export default {
   name: 'TalonariosView',
   mixins: [formatters],
   data: () => ({
      loading: false,
      snackbar: false,
      snackText: '',
      snackColor: 'primary',
      search: '',

      contratos: [],
      cmbCiclo: [],
      cmbCarrera: [],
      selectCiclo: null,
      selectCarrera: null,
      accessCodes: {},
      loadingAccess: {},

      headers: [
         { text: 'N° Talonario', value: 'codigoTalonario', class: 'titulo', width: 130 },
         { text: 'Carnet', value: 'cliente', class: 'titulo', width: 100 },
         { text: 'Alumno', value: 'nombre', class: 'titulo' },
         { text: 'Carrera', value: 'gradoAcademico', class: 'titulo' },
         { text: 'Facultad', value: 'unidadEducativa', class: 'titulo' },
         { text: 'Ciclo', value: 'periodoLectivo', class: 'titulo', width: 100, align: 'center' },
         { text: 'Cód. Carrera', value: 'codigoGradoAcademico', class: 'titulo', width: 110, align: 'center' },
         { text: 'Proc. Grad.', value: 'procesoGraduacion', class: 'titulo', width: 70, align: 'center' },
         { text: 'Estado', value: 'estado', class: 'titulo', width: 100, align: 'center' },
         { text: 'Opciones', value: 'opciones', class: 'titulo', width: 130, align: 'center', sortable: false }
      ]
   }),
   computed: {
      ...authComputed,
      totalActivos() {
         return this.contratos.filter((c) => c.estado === 'A').length
      },
      totalAnulados() {
         return this.contratos.filter((c) => c.estado === 'N').length
      },
      totalGraduacion() {
         return this.contratos.filter((c) => c.procesoGraduacion).length
      }
   },
   watch: {
      selectCiclo() {
         this.selectCarrera = null
         this.listarContratos()
      }
   },
   created() {
      if (this.permController(24, 'ver')) {
         this.listarComboBoxes()
      } else {
         this.$router.push({ name: 'inicio' })
      }
   },
   methods: {
      listarContratos() {
         if (!this.selectCiclo) return
         this.loading = true
         const params = { periodo: this.selectCiclo }
         if (this.selectCarrera) params.carrera = this.selectCarrera
         axios
            .get('api/Contrato/ByPeriodo', { params })
            .then((response) => {
               this.contratos = response.data
            })
            .catch(() => {
               this.snackText = 'Error al cargar los talonarios.'
               this.snackColor = 'snackError'
               this.snackbar = true
            })
            .finally(() => {
               this.loading = false
            })
      },
      async getAccessCode(carnet, tipo) {
         if (this.accessCodes[carnet]) return this.accessCodes[carnet]
         this.$set(this.loadingAccess, carnet, tipo)
         try {
            const response = await axios.get('api/Alumno/Credenciales?carnet=' + carnet)
            const code = response.data[0]?.accessCode ?? ''
            this.$set(this.accessCodes, carnet, code)
            return code
         } catch {
            return ''
         } finally {
            this.$set(this.loadingAccess, carnet, null)
         }
      },
      irColecturiaExterna(item) {
         this.$store.dispatch('login', {
            carnet: item.cliente,
            nombres: item.nombre,
            apellidos: '',
            dui: '',
            email: '',
            telefono: '',
            extranjero: false,
            nit: '',
            direccion: ''
         })
         const routeData = this.$router.resolve({ name: 'colecturiaExterna' })
         window.open(routeData.href, '_blank')
         if (document.activeElement instanceof HTMLElement)
            document.activeElement.blur()
      },
      async irColecturiaDigital(item) {
         const code = await this.getAccessCode(item.cliente, 'colecturia')
         window.open(
            process.env.VUE_APP_CLIENTE +
               'colecturiaDigital?carnet=' + item.cliente +
               '&accesscode=' + code,
            '_blank'
         )
      },
      async irReporteTalonario(item) {
         const code = await this.getAccessCode(item.cliente, 'reporte')
         window.open(
            process.env.VUE_APP_CLIENTE +
               'reporteTalonario?carnet=' + item.cliente +
               '&accesscode=' + code,
            '_blank'
         )
      },
      listarComboBoxes() {
         axios
            .get('api/Ciclos')
            .then((response) => {
               this.cmbCiclo = response.data
               if (this.cmbCiclo.length > 0) {
                  this.selectCiclo = this.cmbCiclo[0].nombre
               }
            })
            .catch(() => {})

         axios
            .get('api/general/CarreraUniversitaria')
            .then((response) => {
               this.cmbCarrera = response.data
            })
            .catch(() => {})
      }
   }
}
</script>
